// =============================================================================
// FILE:
// src/modules/service_crm/satisfaction/components/SatisfactionFormDrawer.js
// =============================================================================

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  X,
  Maximize2,
  Minimize2,
} from "lucide-react";

import serviceSatisfactionService
  from "../../services/serviceSatisfactionService";


// =============================================================================
// RATING OPTIONS
// =============================================================================

const ratingOptions = [

  {
    value: 1,
    label: "1 - Very Poor",
  },

  {
    value: 2,
    label: "2 - Poor",
  },

  {
    value: 3,
    label: "3 - Average",
  },

  {
    value: 4,
    label: "4 - Good",
  },

  {
    value: 5,
    label: "5 - Excellent",
  },
];


// =============================================================================
// COMPONENT
// =============================================================================

export default function SatisfactionFormDrawer({

  open,

  ticket,

  mode = "create",

  satisfactionId,

  onClose,

  onSuccess,

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [isFullscreen, setIsFullscreen] =
    useState(false);

  const [form, setForm] =
    useState({

      followup_date: "",

      followup_executive: "",

      overall_satisfaction_rating: "",

      issue_resolved_rating: "",

      response_time_rating: "",

      service_quality_rating: "",

      would_recommend: false,

      customer_comments: "",
    });


  // ===========================================================================
  // READ ONLY
  // ===========================================================================

  const isViewMode =
    mode === "view";


  // ===========================================================================
  // LOAD
  // ===========================================================================

  useEffect(() => {

    if (
      open &&
      ticket?.ticket_id
    ) {

      loadSatisfaction();
    }

  }, [
    open,
    ticket,
  ]);


  // ===========================================================================
  // LOAD SATISFACTION
  // ===========================================================================

  const loadSatisfaction =
    async () => {

      try {

        if (
          mode === "create"
        ) {

          setForm({

            followup_date: "",

            followup_executive: "",

            overall_satisfaction_rating: "",

            issue_resolved_rating: "",

            response_time_rating: "",

            service_quality_rating: "",

            would_recommend: false,

            customer_comments: "",
          });

          return;
        }

        setLoading(true);

        const data =
          await serviceSatisfactionService
            .getSatisfaction(
              ticket.ticket_id
            );

        if (!data) {
          return;
        }

        setForm({

          followup_date:
            data.followup_date
            || "",

          followup_executive:
            data.followup_executive
            || "",

          overall_satisfaction_rating:
            data.overall_satisfaction_rating
            || "",

          issue_resolved_rating:
            data.issue_resolved_rating
            || "",

          response_time_rating:
            data.response_time_rating
            || "",

          service_quality_rating:
            data.service_quality_rating
            || "",

          would_recommend:
            !!data.would_recommend,

          customer_comments:
            data.customer_comments
            || "",
        });

      } catch (err) {

        console.error(
          "Failed to load satisfaction",
          err
        );

      } finally {

        setLoading(false);
      }
    };


  // ===========================================================================
  // CHANGE
  // ===========================================================================

  const updateField =
    (field, value) => {

      setForm((prev) => ({

        ...prev,

        [field]: value,
      }));
    };


  // ===========================================================================
  // LIVE CALCULATION
  // ===========================================================================

  const satisfactionPercentage =
    useMemo(() => {

      const total =

        Number(
          form.overall_satisfaction_rating || 0
        )

        +

        Number(
          form.issue_resolved_rating || 0
        )

        +

        Number(
          form.response_time_rating || 0
        )

        +

        Number(
          form.service_quality_rating || 0
        );

      return Number(
        (
          (total / 20)
          * 100
        ).toFixed(2)
      );

    }, [form]);


  // ===========================================================================
  // STATUS
  // ===========================================================================

  const satisfactionStatus =
    useMemo(() => {

      if (
        satisfactionPercentage >= 80
      ) {
        return "SATISFIED";
      }

      if (
        satisfactionPercentage >= 60
      ) {
        return "NEUTRAL";
      }

      return "DISSATISFIED";

    }, [
      satisfactionPercentage,
    ]);


  // ===========================================================================
  // SAVE
  // ===========================================================================

  const handleSave =
    async () => {

      try {
        if (
            !form.overall_satisfaction_rating ||
            !form.issue_resolved_rating ||
            !form.response_time_rating ||
            !form.service_quality_rating
            ) {

            alert(
                "All ratings are mandatory."
            );

            return;
            }

        setSaving(true);

        const payload = {

          followup_date:
            form.followup_date,

          followup_executive:
            form.followup_executive,

          overall_satisfaction_rating:
            Number(
              form.overall_satisfaction_rating
            ),

          issue_resolved_rating:
            Number(
              form.issue_resolved_rating
            ),

          response_time_rating:
            Number(
              form.response_time_rating
            ),

          service_quality_rating:
            Number(
              form.service_quality_rating
            ),

          would_recommend:
            form.would_recommend,

          customer_comments:
            form.customer_comments,
        };

        if (
          mode === "edit"
        ) {

          await serviceSatisfactionService
            .updateSatisfaction(
              satisfactionId,
              payload
            );

        } else {

          await serviceSatisfactionService
            .createSatisfaction(
              ticket.ticket_id,
              payload
            );
        }

        if (onSuccess) {
          onSuccess();
        }
        onClose();

      } catch (err) {

        console.error(
          "Save failed",
          err
        );

      } finally {

        setSaving(false);
      }
    };


  // ===========================================================================
  // CLOSE
  // ===========================================================================

  if (!open) {
    return null;
  }

  return (

    <div style={styles.overlay}>

      <div
        style={{
          ...styles.drawer,

          width:
            isFullscreen
              ? "100%"
              : "72%",
        }}
      >

        {/* HEADER */}

        <div style={styles.header}>

          <div>

            <div style={styles.title}>
              Customer Satisfaction
            </div>

            <div style={styles.subtitle}>
              {ticket?.ticket_no || "-"}
            </div>

          </div>

          <div style={styles.headerActions}>

            <button
              style={styles.iconBtn}
              onClick={() =>
                setIsFullscreen(
                  !isFullscreen
                )
              }
            >

              {
                isFullscreen

                  ? <Minimize2 size={18} />

                  : <Maximize2 size={18} />
              }

            </button>

            <button
              style={styles.iconBtn}
              onClick={onClose}
            >
              <X size={18} />
            </button>

          </div>

        </div>

        {/* BODY */}

        <div style={styles.body}>

          {loading ? (

            <div style={styles.loader}>
              Loading...
            </div>

          ) : (

  <>

  {/* ========================================================= */}
  {/* SUMMARY */}
              {/* ========================================================= */}

              <div style={styles.summaryGrid}>

                <SummaryCard
                  label="Ticket No"
                  value={ticket?.ticket_no}
                />

                <SummaryCard
                  label="Customer"
                  value={ticket?.customer_name}
                />

                <SummaryCard
                  label="Engineer"
                  value={ticket?.assigned_engineer}
                />

                <SummaryCard
                    label="Satisfaction %"
                    value={
                        ticket?.satisfaction_percentage
                        || "-"
                    }
                    />

                <SummaryCard
                  label="Closed Date"
                  value={ticket?.closed_at}
                />

              </div>


              {/* ========================================================= */}
              {/* FOLLOW UP */}
              {/* ========================================================= */}

              <div style={styles.card}>

                <div style={styles.cardTitle}>
                  Follow-up Information
                </div>

                <div style={styles.formGrid}>

                  <div>

                    <label style={styles.label}>
                      Follow-up Date
                    </label>

                    <input
                      type="date"
                      value={form.followup_date}
                      disabled={isViewMode}
                      onChange={(e) =>
                        updateField(
                          "followup_date",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />

                  </div>

                  <div>

                    <label style={styles.label}>
                      Follow-up Executive
                    </label>

                    <input
                      type="text"
                      value={
                        form.followup_executive
                      }
                      disabled={isViewMode}
                      onChange={(e) =>
                        updateField(
                          "followup_executive",
                          e.target.value
                        )
                      }
                      style={styles.input}
                    />

                  </div>

                </div>

              </div>


              {/* ========================================================= */}
              {/* RATINGS */}
              {/* ========================================================= */}

              <div style={styles.card}>

                <div style={styles.cardTitle}>
                  Customer Satisfaction Ratings
                </div>

                <div style={styles.formGrid}>


                  <RatingField
                    label="Overall Satisfaction"
                    value={
                      form.overall_satisfaction_rating
                    }
                    disabled={isViewMode}
                    onChange={(value) =>
                      updateField(
                        "overall_satisfaction_rating",
                        value
                      )
                    }
                  />

                  <RatingField
                    label="Issue Resolved"
                    value={
                      form.issue_resolved_rating
                    }
                    disabled={isViewMode}
                    onChange={(value) =>
                      updateField(
                        "issue_resolved_rating",
                        value
                      )
                    }
                  />

                  <RatingField
                    label="Response Time"
                    value={
                      form.response_time_rating
                    }
                    disabled={isViewMode}
                    onChange={(value) =>
                      updateField(
                        "response_time_rating",
                        value
                      )
                    }
                  />

                  <RatingField
                    label="Service Quality"
                    value={
                      form.service_quality_rating
                    }
                    disabled={isViewMode}
                    onChange={(value) =>
                      updateField(
                        "service_quality_rating",
                        value
                      )
                    }
                  />

                </div>

              </div>


              {/* ========================================================= */}
              {/* RECOMMENDATION */}
              {/* ========================================================= */}

              <div style={styles.card}>

                <div style={styles.cardTitle}>
                  Recommendation
                </div>

                <div style={styles.radioContainer}>

                  <label>

                    <input
                      type="radio"
                      checked={
                        form.would_recommend === true
                      }
                      disabled={isViewMode}
                      onChange={() =>
                        updateField(
                          "would_recommend",
                          true
                        )
                      }
                    />

                    {" "}Yes

                  </label>

                  <label>

                    <input
                      type="radio"
                      checked={
                        form.would_recommend === false
                      }
                      disabled={isViewMode}
                      onChange={() =>
                        updateField(
                          "would_recommend",
                          false
                        )
                      }
                    />

                    {" "}No

                  </label>

                </div>

              </div>


              {/* ========================================================= */}
              {/* COMMENTS */}
              {/* ========================================================= */}

              <div style={styles.card}>

                <div style={styles.cardTitle}>
                  Customer Comments
                </div>

                <textarea

                  rows={5}

                  value={
                    form.customer_comments
                  }

                  disabled={isViewMode}

                  onChange={(e) =>
                    updateField(
                      "customer_comments",
                      e.target.value
                    )
                  }

                  style={styles.textarea}
                />

              </div>


              {/* ========================================================= */}
              {/* PREVIEW */}
              {/* ========================================================= */}

              <div style={styles.card}>

                <div style={styles.cardTitle}>
                  Satisfaction Preview
                </div>

                <div style={styles.previewGrid}>

                  <SummaryCard
                    label="Satisfaction %"
                    value={
                      `${satisfactionPercentage}%`
                    }
                  />

                  <SummaryCard
                    label="Status"
                    value={
                      satisfactionStatus
                    }
                  />

                </div>

              </div>

            </>
          )}

        </div>


        {/* ============================================================= */}
        {/* FOOTER */}
        {/* ============================================================= */}

        <div style={styles.footer}>

          <button
            style={styles.cancelBtn}
            onClick={onClose}
          >
            Close
          </button>

          {

            !isViewMode && (

              <button
                style={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >

                {
                  saving
                    ? "Saving..."
                    : "Save Satisfaction"
                }

              </button>
            )
          }

        </div>

      </div>

    </div>
  );
}


// ============================================================================
// RATING FIELD
// ============================================================================

function RatingField({

  label,
  value,
  disabled,
  onChange,

}) {

  return (

    <div>

      <label style={styles.label}>
        {label}
      </label>

      <select

        value={value}

        disabled={disabled}

        onChange={(e) =>
          onChange(
            e.target.value
          )
        }

        style={styles.input}
      >

        <option value="">
          Select Rating
        </option>

        {

          ratingOptions.map((item) => (

            <option
              key={item.value}
              value={item.value}
            >
              {item.label}
            </option>

          ))
        }

      </select>

    </div>
  );
}


// ============================================================================
// SUMMARY CARD
// ============================================================================

function SummaryCard({

  label,
  value,

}) {

  return (

    <div style={styles.summaryCard}>

      <div style={styles.summaryLabel}>
        {label}
      </div>

      <div style={styles.summaryValue}>
        {value || "-"}
      </div>

    </div>
  );
}
// ============================================================================
// STYLES
// ============================================================================

const styles = {

  overlay: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(0,0,0,0.35)",

    display: "flex",

    justifyContent: "flex-end",

    zIndex: 99999,
  },

  drawer: {

    height: "100vh",

    background: "#f5f7fb",

    display: "flex",

    flexDirection: "column",

    transition:
      "width 0.25s ease",
  },

  header: {

    background: "#ffffff",

    padding: 20,

    borderBottom:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",
  },

  title: {

    fontSize: 22,

    fontWeight: 700,

    color: "#111827",
  },

  subtitle: {

    marginTop: 6,

    fontSize: 13,

    color: "#6b7280",
  },

  headerActions: {

    display: "flex",

    gap: 10,
  },

  iconBtn: {

    width: 36,

    height: 36,

    borderRadius: 8,

    border:
      "1px solid #d1d5db",

    background: "#fff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    cursor: "pointer",
  },

  body: {

    flex: 1,

    overflow: "auto",

    padding: 20,

    display: "flex",

    flexDirection: "column",

    gap: 20,
  },

  loader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center",

    fontSize: 14,

    color: "#6b7280",
  },

  summaryGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(5, 1fr)",

    gap: 16,
  },

  summaryCard: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 18,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)",
  },

  summaryLabel: {

    fontSize: 12,

    color: "#6b7280",

    marginBottom: 8,
  },

  summaryValue: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827",
  },

  card: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)",
  },

  cardTitle: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827",

    marginBottom: 18,
  },

  formGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(2, 1fr)",

    gap: 20,
  },

  label: {

    display: "block",

    marginBottom: 8,

    fontSize: 13,

    fontWeight: 600,

    color: "#374151",
  },

  input: {

    width: "100%",

    height: 42,

    border:
      "1px solid #d1d5db",

    borderRadius: 10,

    padding: "0 12px",

    fontSize: 14,

    background: "#ffffff",
  },

  textarea: {

    width: "100%",

    border:
      "1px solid #d1d5db",

    borderRadius: 10,

    padding: 12,

    resize: "vertical",

    fontSize: 14,

    background: "#ffffff",
  },

  radioContainer: {

    display: "flex",

    gap: 30,

    alignItems: "center",

    fontSize: 14,
  },

  previewGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(2, 1fr)",

    gap: 16,
  },

  footer: {

    background: "#ffffff",

    borderTop:
      "1px solid #e5e7eb",

    padding: 16,

    display: "flex",

    justifyContent:
      "flex-end",

    gap: 12,
  },

  cancelBtn: {

    height: 40,

    padding: "0 18px",

    border:
      "1px solid #d1d5db",

    borderRadius: 10,

    background: "#ffffff",

    cursor: "pointer",

    fontWeight: 600,
  },

  saveBtn: {

    height: 40,

    padding: "0 18px",

    border: "none",

    borderRadius: 10,

    background: "#2563eb",

    color: "#ffffff",

    cursor: "pointer",

    fontWeight: 600,
  },
};